// 用户状态管理 - 集成真实后端API
import { defineStore } from 'pinia'
import ActionAPI, { ActionUserAPI } from '../../api/comfyui-action/index.js'

export const useUserStore = defineStore('user', {
  state: () => ({
    userInfo: {
      id: null,
      username: '',
      phone: '',
      email: '',
      avatar_url: '',
      company_name: '',
      gender: '',
      total_api_calls: 0,
      status: '',
      created_at: null,
      updated_at: null
    },
    isLoggedIn: false,
    token: '',
    userQuota: {
      todayCalls: 0,
      dailyLimit: 10,
      totalCalls: 0,
      remainingCalls: 10,
      todayRemainingCalls: 10,
      remainingCallsLastRefresh: null
    },
    membershipInfo: null,
    packageInfo: []
  }),

  actions: {
    // 登录
    async login(loginData) {
      try {
        const { phone, sms_code } = loginData
        const response = await ActionUserAPI.loginWithPhone({ phone, sms_code })
        
        if (response.success) {
          this.token = response.data.token
          this.userInfo = {
            id: response.data.user_id,
            ...response.data.userinfo
          }
          this.isLoggedIn = true
          
          // 保存到本地存储
          this.saveToStorage()
          
          // 获取用户配额信息
          await this.fetchUserQuota()
          
          return { success: true, data: response.data }
        } else {
          return { success: false, error: response.error, code: response.code }
        }
      } catch (error) {
        console.error('登录失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 注册
    async register(registerData) {
      try {
        const { phone, sms_code, company_name, email } = registerData
        const response = await ActionUserAPI.registerWithPhone({ 
          phone, 
          sms_code, 
          company_name, 
          email 
        })
        
        if (response.success) {
          // 注册成功后自动登录
          return await this.login({ phone, sms_code })
        } else {
          return { success: false, error: response.error, code: response.code }
        }
      } catch (error) {
        console.error('注册失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 发送短信验证码
    async sendSmsCode(phone) {
      try {
        const response = await ActionUserAPI.sendSmsCode(phone)
        return response
      } catch (error) {
        console.error('发送验证码失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 退出登录
    async logout() {
      try {
        // 尝试调用后端退出登录接口（如果接口可用）
        if (this.token) {
          try {
            const response = await ActionAPI.logout()
            if (response.success) {
              console.log('后端退出登录成功')
            } else {
              console.log('后端退出登录返回错误，但继续执行本地退出:', response.error)
            }
          } catch (apiError) {
            // 如果是405错误或其他API错误，仅记录日志，不影响本地退出
            console.log('后端退出登录接口不可用，仅执行本地退出:', apiError.message || apiError)
          }
        }
      } catch (error) {
        // 任何其他错误都不应该阻止本地退出
        console.log('退出登录过程中出现错误，继续执行本地退出:', error)
      }
      
      // 清除本地状态（无论后端接口是否成功）
      this.token = ''
      this.userInfo = {
        id: null,
        username: '',
        phone: '',
        email: '',
        avatar_url: '',
        company_name: '',
        gender: '',
        total_api_calls: 0,
        status: '',
        created_at: null,
        updated_at: null
      }
      this.isLoggedIn = false
      this.userQuota = {
        todayCalls: 0,
        dailyLimit: 10,
        totalCalls: 0,
        remainingCalls: 10,
        todayRemainingCalls: 10,
        remainingCallsLastRefresh: null
      }
      this.membershipInfo = null
      this.packageInfo = []
      
      // 清除本地存储
      this.clearStorage()
      
      console.log('用户退出登录完成')
    },

    // 更新用户信息
    async updateUserInfo(newInfo) {
      try {
        if (!this.userInfo.id) {
          return { success: false, error: '用户未登录' }
        }

        const response = await ActionAPI.updateUserInfo(this.userInfo.id, newInfo)
        
        if (response.success) {
          // 更新本地状态
          this.userInfo = { ...this.userInfo, ...response.data }
          this.saveToStorage()
          
          return { success: true, data: response.data }
        } else {
          return { success: false, error: response.error }
        }
      } catch (error) {
        console.error('更新用户信息失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 获取用户配额信息
    async fetchUserQuota() {
      try {
        if (!this.userInfo.id) {
          return { success: false, error: '用户未登录' }
        }

        const response = await ActionAPI.getUserQuota(this.userInfo.id)
        
        if (response.success) {
          this.userQuota = response.data
          return { success: true, data: response.data }
        } else {
          return { success: false, error: response.error }
        }
      } catch (error) {
        console.error('获取用户配额失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 获取用户会员信息
    async fetchMembershipInfo() {
      try {
        if (!this.userInfo.id) {
          return { success: false, error: '用户未登录' }
        }

        const response = await ActionAPI.getUserMemberships(this.userInfo.id)
        
        if (response.success) {
          this.membershipInfo = response.data.user_memberships.length > 0 
            ? response.data.user_memberships[0] 
            : null
          return { success: true, data: response.data }
        } else {
          return { success: false, error: response.error }
        }
      } catch (error) {
        console.error('获取会员信息失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 获取用户加量包信息
    async fetchPackageInfo() {
      try {
        if (!this.userInfo.id) {
          return { success: false, error: '用户未登录' }
        }

        const response = await ActionAPI.getUserPackages(this.userInfo.id)
        
        if (response.success) {
          this.packageInfo = response.data.user_packages || []
          return { success: true, data: response.data }
        } else {
          return { success: false, error: response.error }
        }
      } catch (error) {
        console.error('获取加量包信息失败:', error)
        return { success: false, error: error.message }
      }
    },

    // 初始化用户状态（从本地存储恢复）
    async initUserState() {
      try {
        console.log('开始初始化用户状态...')
        
        // 获取token
        const token = uni.getStorageSync('token') || localStorage.getItem('token')
        console.log('从本地存储获取token:', token ? '存在' : '不存在')
        
        // 获取用户信息 - 统一JSON字符串格式
        let userInfo = null
        try {
          // 优先从uni storage获取
          const uniUserInfo = uni.getStorageSync('userInfo')
          if (uniUserInfo) {
            userInfo = typeof uniUserInfo === 'string' ? JSON.parse(uniUserInfo) : uniUserInfo
          } else {
            // 如果uni storage没有，尝试localStorage
            const localUserInfo = localStorage.getItem('userInfo')
            if (localUserInfo) {
              userInfo = JSON.parse(localUserInfo)
            }
          }
        } catch (e) {
          console.warn('解析userInfo失败:', e)
          userInfo = null
        }
        
        console.log('从本地存储获取userInfo:', userInfo ? '存在' : '不存在', userInfo)
        
        if (token && userInfo && userInfo.id) {
          this.token = token
          this.userInfo = userInfo
          this.isLoggedIn = true
          
          console.log('用户状态恢复成功:', {
            userId: userInfo.id,
            phone: userInfo.phone,
            isLoggedIn: this.isLoggedIn
          })
          
          // 获取最新的配额信息
          this.fetchUserQuota()
        } else {
          console.log('用户状态恢复失败 - 数据不完整:', { token: !!token, userInfo: !!userInfo, userId: userInfo?.id })
        }
      } catch (error) {
        console.error('初始化用户状态失败:', error)
        this.clearStorage()
      }
    },

    // 保存到本地存储
    saveToStorage() {
      try {
        console.log('保存用户状态到本地存储:', {
          token: this.token ? '存在' : '不存在',
          userId: this.userInfo?.id,
          phone: this.userInfo?.phone
        })
        
        // 保存token
        uni.setStorageSync('token', this.token)
        localStorage.setItem('token', this.token)
        
        // 保存用户信息（统一为JSON字符串格式）
        const userInfoStr = JSON.stringify(this.userInfo)
        uni.setStorageSync('userInfo', userInfoStr)
        localStorage.setItem('userInfo', userInfoStr)
        
        console.log('用户状态保存成功')
      } catch (error) {
        console.error('保存到本地存储失败:', error)
      }
    },

    // 清除本地存储
    clearStorage() {
      try {
        uni.removeStorageSync('token')
        uni.removeStorageSync('userInfo')
        
        // 同时清除localStorage
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
      } catch (error) {
        console.error('清除本地存储失败:', error)
      }
    }
  },

  getters: {
    // 是否为VIP用户
    isVip: (state) => {
      return state.membershipInfo && 
             state.membershipInfo.status === 'active' && 
             new Date(state.membershipInfo.expires_at) > new Date()
    },

    // 获取用户显示名称
    displayName: (state) => {
      return state.userInfo.username || 
             (state.userInfo.phone ? `用户${state.userInfo.phone.slice(-4)}` : '用户')
    },

    // 今日剩余调用次数（使用新的累加逻辑）
    remainingCalls: (state) => {
      // 优先使用 todayRemainingCalls，如果没有则回退到旧逻辑
      if (state.userQuota.todayRemainingCalls !== undefined) {
        return Math.max(0, state.userQuota.todayRemainingCalls)
      }
      // 如果没有新字段，使用 remainingCalls，最后回退到旧逻辑
      if (state.userQuota.remainingCalls !== undefined) {
        return Math.max(0, state.userQuota.remainingCalls)
      }
      // 旧逻辑：dailyLimit - todayCalls
      return Math.max(0, state.userQuota.dailyLimit - state.userQuota.todayCalls)
    },

    // 是否可以调用API（使用新的累加逻辑）
    canCallApi: (state) => {
      // 优先使用 todayRemainingCalls，如果没有则回退到旧逻辑
      if (state.userQuota.todayRemainingCalls !== undefined) {
        return state.userQuota.todayRemainingCalls > 0
      }
      // 如果没有新字段，使用 remainingCalls，最后回退到旧逻辑
      if (state.userQuota.remainingCalls !== undefined) {
        return state.userQuota.remainingCalls > 0
      }
      // 旧逻辑：dailyLimit > todayCalls
      return state.userQuota.dailyLimit > state.userQuota.todayCalls
    }
  }
})

// 导出 Hook 函数以兼容原有代码
export const useUserStoreHook = () => {
  return useUserStore()
}